import React, { useState } from 'react';
import { Search, MapPin, Calendar, Star, ChevronRight } from 'lucide-react';
import LanguageSelectionPreview from './LanguageSelectionPreview';
import { ThemeColorPalette } from '../../types';
import { DEFAULT_THEME_COLORS, mergeThemeColors } from '../../constants/themeColors';

interface MobilePreviewProps {
  appName: string;
  appIcon?: string | null;
  themeColors: ThemeColorPalette;
  homeBgPreview?: string | null;
  onboardBgPreview?: string | null;
}

const MobilePreview: React.FC<MobilePreviewProps> = ({
  appName,
  appIcon,
  themeColors,
  homeBgPreview,
  onboardBgPreview,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const mergedColors = mergeThemeColors(themeColors);
  
  // Map brand colors to the design
  const primaryColor = mergedColors.PRIMARY || DEFAULT_THEME_COLORS.PRIMARY;
  const secondaryColor = mergedColors.SECONDARY || DEFAULT_THEME_COLORS.SECONDARY;
  const accentColor = mergedColors.ACCENT || DEFAULT_THEME_COLORS.ACCENT;
  const blue200Color = mergedColors.BLUE_200 || DEFAULT_THEME_COLORS.BLUE_200;
  
  const pages = [
    { id: 0, name: 'Home' },
    { id: 1, name: 'Language' }
  ];
  const whiteColor = mergedColors.WHITE || DEFAULT_THEME_COLORS.WHITE;
  const blackColor = mergedColors.BLACK || DEFAULT_THEME_COLORS.BLACK;
  const greyColor = mergedColors.GREY || DEFAULT_THEME_COLORS.GREY;
  const backgroundWhite = mergedColors.BACKGROUND_WHITE || DEFAULT_THEME_COLORS.BACKGROUND_WHITE;
  const gray800Color = mergedColors.GRAY_800 || DEFAULT_THEME_COLORS.GRAY_800;
  const gray600Color = mergedColors.GRAY_600 || DEFAULT_THEME_COLORS.GRAY_600;
  const white200Color = mergedColors.WHITE_200 || DEFAULT_THEME_COLORS.WHITE_200;
  const redColor = mergedColors.RED || DEFAULT_THEME_COLORS.RED;
  const goldColor = '#FFD700'; // Fixed gold color for stars

  return (
    <div className="flex flex-col items-center space-y-4">
      <style dangerouslySetInnerHTML={{
        __html: `
          .mobile-preview-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `
      }} />
      <h4 className="text-lg font-medium text-gray-900">Live Preview</h4>
      
      {/* Mobile Frame */}
      <div className="relative">
        {/* Phone Frame */}
        <div className="w-72 h-[580px] bg-gray-800 rounded-[2rem] p-1.5 shadow-2xl">
          <div className="w-full h-full bg-white rounded-[1.5rem] overflow-hidden relative">
            {currentPage === 0 ? (
              // Home Page Content
              <>
            {/* Status Bar */}
            <div className="h-5 bg-gray-100 flex items-center justify-between px-3 text-xs">
              <span className="font-medium">9:41</span>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-1.5 bg-gray-400 rounded-sm"></div>
                <div className="w-3 h-1.5 bg-gray-400 rounded-sm"></div>
                <div className="w-3 h-1.5 bg-gray-400 rounded-sm"></div>
              </div>
            </div>

            {/* App Content */}
            <div 
              className="flex-1 overflow-y-auto mobile-preview-scrollbar" 
              style={{ 
                backgroundColor: backgroundWhite,
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
              }}
            >
              {/* Header Section */}
              <div 
                className="relative h-32 flex items-center justify-center"
                style={{
                  background: homeBgPreview 
                    ? `url(${homeBgPreview}) no-repeat center center`
                    : `url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') no-repeat center center`,
                  backgroundSize: 'cover'
                }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                <h1 className="relative z-10 text-white text-lg font-bold text-center px-2">
                  {appName}
                </h1>
              </div>

              {/* Search Bar */}
              <div className="px-3 -mt-4 relative z-10">
                <div 
                  className="bg-white rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg"
                  style={{ boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.08)' }}
                >
                  <Search className="w-4 h-4" style={{ color: accentColor }} />
                  <input 
                    type="text" 
                    placeholder="Search tours..." 
                    className="flex-1 border-none outline-none text-xs"
                    style={{ color: gray800Color }}
                  />
                </div>
              </div>

              {/* Main Content */}
              <div className="px-3 py-3 space-y-4">
                {/* Continue Tour Section */}
                <div 
                  className="bg-white rounded-lg p-2 border-l-4"
                  style={{ 
                    borderLeftColor: primaryColor,
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{
                        background: `url('https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') no-repeat center center`,
                        backgroundSize: 'cover'
                      }}
                    >
                      🏛️
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-medium mb-0.5 truncate" style={{ color: accentColor }}>
                        Continue your tour
                      </h3>
                      <p className="text-xs truncate" style={{ color: gray800Color }}>
                        Historic Downtown Walking Tour
                      </p>
                    </div>
                    <button 
                      className="px-2 py-1 rounded-full text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Resume
                    </button>
                  </div>
                </div>

                {/* Tour Bundles Section */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4" style={{ color: accentColor }}>
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path>
                        </svg>
                      </div>
                      <h2 className="text-sm font-bold" style={{ color: accentColor }}>
                        Tour Bundles
                      </h2>
                    </div>
                    <a href="#" className="flex items-center gap-1 text-xs" style={{ color: accentColor }}>
                      See all
                      <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>

                  <div 
                    className="flex gap-3 overflow-x-auto pb-1 mobile-preview-scrollbar"
                    style={{
                      scrollbarWidth: 'none', // Firefox
                      msOverflowStyle: 'none', // IE and Edge
                    }}
                  >
                    {/* Bundle Card 1 - London */}
                    <div 
                      className="bg-white rounded-lg w-36 flex-shrink-0 overflow-hidden"
                      style={{ 
                        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                        border: `1px solid ${white200Color}`
                      }}
                    >
                      <div 
                        className="h-20 relative"
                        style={{
                          background: `url('https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') no-repeat center center`,
                          backgroundSize: 'cover'
                        }}
                      >
                         <div 
                           className="absolute top-1 right-1 px-2 py-0.5 rounded text-white text-xs font-bold"
                           style={{ backgroundColor: secondaryColor }}
                         >
                           Save 50%
                         </div>
                      </div>
                      <div className="p-2">
                        <h3 className="font-bold text-xs mb-1" style={{ color: accentColor }}>
                          London Complete Package
                        </h3>
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-2 h-2" style={{ color: primaryColor }} />
                            <span className="text-xs" style={{ color: gray800Color }}>London, UK</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-2 h-2" style={{ color: gray800Color }} />
                            <span className="text-xs" style={{ color: gray800Color }}>10 tours included</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <div className="text-xs line-through" style={{ color: gray600Color }}>$99.90</div>
                            <div className="font-bold text-xs" style={{ color: accentColor }}>$49.99</div>
                          </div>
                          <button 
                            className="px-2 py-1 rounded text-white text-xs font-bold"
                            style={{ backgroundColor: primaryColor }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Bundle Card 2 - Paris */}
                    <div 
                      className="bg-white rounded-lg w-36 flex-shrink-0 overflow-hidden"
                      style={{ 
                        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                        border: `1px solid ${white200Color}`
                      }}
                    >
                      <div 
                        className="h-20 relative"
                        style={{
                          background: `url('https://images.unsplash.com/photo-1499856871958-5b9627545d1a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') no-repeat center center`,
                          backgroundSize: 'cover'
                        }}
                      >
                         <div 
                           className="absolute top-1 right-1 px-2 py-0.5 rounded text-white text-xs font-bold"
                           style={{ backgroundColor: secondaryColor }}
                         >
                           Save 30%
                         </div>
                      </div>
                      <div className="p-2">
                        <h3 className="font-bold text-xs mb-1" style={{ color: accentColor }}>
                          Paris City Explorer
                        </h3>
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-2 h-2" style={{ color: primaryColor }} />
                            <span className="text-xs" style={{ color: gray800Color }}>Paris, France</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-2 h-2" style={{ color: gray800Color }} />
                            <span className="text-xs" style={{ color: gray800Color }}>8 tours included</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <div className="text-xs line-through" style={{ color: gray600Color }}>$79.90</div>
                            <div className="font-bold text-xs" style={{ color: accentColor }}>$55.99</div>
                          </div>
                          <button 
                            className="px-2 py-1 rounded text-white text-xs font-bold"
                            style={{ backgroundColor: primaryColor }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Bundle Card 4 - Barcelona */}
                    <div 
                      className="bg-white rounded-lg w-36 flex-shrink-0 overflow-hidden"
                      style={{ 
                        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                        border: `1px solid ${white200Color}`
                      }}
                    >
                      <div 
                        className="h-20 relative"
                        style={{
                          background: `url('https://images.unsplash.com/photo-1539037116277-4db20889f2d4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') no-repeat center center`,
                          backgroundSize: 'cover'
                        }}
                      >
                         <div 
                           className="absolute top-1 right-1 px-2 py-0.5 rounded text-white text-xs font-bold"
                           style={{ backgroundColor: secondaryColor }}
                         >
                           Save 40%
                         </div>
                      </div>
                      <div className="p-2">
                        <h3 className="font-bold text-xs mb-1" style={{ color: accentColor }}>
                          Barcelona Art & Culture
                        </h3>
                        <div className="space-y-1 mb-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-2 h-2" style={{ color: primaryColor }} />
                            <span className="text-xs" style={{ color: gray800Color }}>Barcelona, Spain</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-2 h-2" style={{ color: gray800Color }} />
                            <span className="text-xs" style={{ color: gray800Color }}>6 tours included</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <div className="text-xs line-through" style={{ color: gray600Color }}>$69.90</div>
                            <div className="font-bold text-xs" style={{ color: accentColor }}>$41.99</div>
                          </div>
                          <button 
                            className="px-2 py-1 rounded text-white text-xs font-bold"
                            style={{ backgroundColor: primaryColor }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Featured Tour Section */}
                <div 
                  className="bg-white rounded-lg overflow-hidden"
                  style={{ 
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                    border: `1px solid ${white200Color}`
                  }}
                >
                  <div 
                    className="h-24 relative"
                    style={{
                      background: `url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80') no-repeat center center`,
                      backgroundSize: 'cover'
                    }}
                  >
                    <div 
                      className="absolute top-1 right-1 bg-white px-1 py-0.5 rounded-full flex items-center gap-1 text-xs font-semibold"
                      style={{ color: blackColor }}
                    >
                      <Star className="w-2 h-2" style={{ color: goldColor }} />
                      4.8
                    </div>
                  </div>
                  <div className="p-2">
                    <h3 className="font-bold text-xs mb-2" style={{ color: accentColor }}>
                      Isle of Sky Self Guided Driving Tour
                    </h3>
                    <div className="flex justify-between items-end mb-2">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 rounded-sm" style={{ background: 'linear-gradient(45deg, #012169, #ffffff, #c8102e)' }}></div>
                        <div className="w-3 h-2 rounded-sm" style={{ background: 'linear-gradient(45deg, #009246, #ffffff, #ce2b37)' }}></div>
                        <div className="w-3 h-2 rounded-sm" style={{ background: 'linear-gradient(45deg, #000000, #dd0000, #ffce00)' }}></div>
                        <span className="text-xs ml-1" style={{ color: greyColor }}>+2</span>
                      </div>
                      <div className="font-bold text-xs" style={{ color: accentColor }}>$19.99</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <a href="#" className="flex items-center gap-1 text-xs font-bold" style={{ color: secondaryColor }}>
                        View Details
                        <ChevronRight className="w-3 h-3" />
                      </a>
                      <button 
                        className="px-3 py-1 rounded-full text-white text-xs font-bold"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Buy now
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Navigation */}
              <div 
                className="absolute bottom-0 left-0 right-0 h-12 flex items-center justify-around border-t px-2"
                style={{ 
                  backgroundColor: whiteColor,
                  borderTopColor: white200Color
                }}
              >
                <div className="flex flex-col items-center gap-0.5">
                  <div className="w-4 h-4" style={{ color: primaryColor }}>
                    <svg fill="currentColor" viewBox="0 0 25 24">
                      <path d="M15.2188 15.2812L8.46875 17.8594C7.53125 18.2344 6.64062 17.2969 7.01562 16.4062L9.59375 9.65625C9.78125 9.23438 10.0625 8.95312 10.4844 8.76562L17.2344 6.1875C18.1719 5.8125 19.0625 6.70312 18.6875 7.64062L16.1094 14.3906C15.9219 14.8125 15.6406 15.0938 15.2188 15.2812ZM12.8281 10.5C12.0312 10.5 11.3281 11.2031 11.3281 12C11.3281 12.8438 12.0312 13.5 12.8281 13.5C13.6719 13.5 14.375 12.8438 14.375 12C14.375 11.2031 13.6719 10.5 12.8281 10.5ZM24.875 12C24.875 18.6562 19.4844 24 12.875 24C6.21875 24 0.875 18.6562 0.875 12C0.875 5.39062 6.21875 0 12.875 0C19.4844 0 24.875 5.39062 24.875 12ZM12.875 2.25C7.48438 2.25 3.125 6.65625 3.125 12C3.125 17.3906 7.48438 21.75 12.875 21.75C18.2188 21.75 22.625 17.3906 22.625 12C22.625 6.65625 18.2188 2.25 12.875 2.25Z"/>
                    </svg>
                  </div>
                  <span className="text-xs font-medium" style={{ color: primaryColor }}>Explore</span>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <div className="w-4 h-4" style={{ color: gray600Color }}>
                    <svg fill="currentColor" viewBox="0 0 25 22">
                      <path d="M12.625 0.5C19.3281 0.5 24.3906 6.07812 24.625 12.5L24.5781 17.75C24.5781 17.7969 24.5781 17.8438 24.5781 17.8906C24.4375 19.9062 22.7969 21.5 20.7344 21.5C19.5625 21.5 18.625 20.5625 18.625 19.3906V13.1562C18.625 11.9844 19.5625 11 20.7344 11C21.2969 11 21.8125 11.1406 22.2812 11.3281C21.6719 6.5 17.5938 2.79688 12.625 2.79688C7.60938 2.79688 3.53125 6.5 2.92188 11.3281C3.39062 11.1406 3.90625 11 4.46875 11C5.64062 11 6.625 11.9844 6.625 13.1562V19.3906C6.625 20.5625 5.64062 21.5 4.46875 21.5C2.40625 21.5 0.71875 19.9062 0.625 17.8906C0.625 17.8438 0.625 17.7969 0.625 17.75V12.5C0.8125 6.125 5.875 0.5 12.625 0.5ZM4.375 13.2969C3.53125 13.3438 2.875 14.0469 2.875 14.8906V17.6562C2.875 18.5 3.53125 19.2031 4.375 19.25V13.2969ZM20.875 19.25C21.6719 19.2031 22.375 18.5 22.375 17.6562V14.8906C22.375 14.0469 21.6719 13.3438 20.875 13.2969V19.25Z"/>
                    </svg>
                  </div>
                  <span className="text-xs" style={{ color: gray600Color }}>My Tours</span>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <div className="w-4 h-4" style={{ color: gray600Color }}>
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                    </svg>
                  </div>
                  <span className="text-xs" style={{ color: gray600Color }}>Support</span>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <div className="w-4 h-4" style={{ color: gray600Color }}>
                    <svg fill="currentColor" viewBox="0 0 22 24">
                      <path d="M15.2969 14.25C18.7656 14.25 21.625 17.1094 21.625 20.5781V21.75C21.625 23.0156 20.5938 24 19.375 24H2.875C1.60938 24 0.625 23.0156 0.625 21.75V20.5781C0.625 17.1094 3.4375 14.25 6.90625 14.25C8.26562 14.25 8.875 15 11.125 15C13.3281 15 13.9375 14.25 15.2969 14.25ZM19.375 21.75V20.5781C19.375 18.3281 17.5469 16.5 15.2969 16.5C14.5938 16.5 13.5156 17.25 11.125 17.25C8.6875 17.25 7.60938 16.5 6.90625 16.5C4.65625 16.5 2.875 18.3281 2.875 20.5781V21.75H19.375ZM11.125 13.5C7.375 13.5 4.375 10.5 4.375 6.75C4.375 3.04688 7.375 0 11.125 0C14.8281 0 17.875 3.04688 17.875 6.75C17.875 10.5 14.8281 13.5 11.125 13.5ZM11.125 2.25C8.64062 2.25 6.625 4.3125 6.625 6.75C6.625 9.23438 8.64062 11.25 11.125 11.25C13.5625 11.25 15.625 9.23438 15.625 6.75C15.625 4.3125 13.5625 2.25 11.125 2.25Z"/>
                    </svg>
                  </div>
                  <span className="text-xs" style={{ color: gray600Color }}>Profile</span>
                </div>
              </div>
            </div>
              </>
            ) : (
              // Language Selection Page
              <>
                {/* Status Bar */}
                <div className="h-5 bg-gray-100 flex items-center justify-between px-3 text-xs">
                  <span className="font-medium">9:41</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-1.5 bg-gray-400 rounded-sm"></div>
                    <div className="w-3 h-1.5 bg-gray-400 rounded-sm"></div>
                    <div className="w-3 h-1.5 bg-gray-400 rounded-sm"></div>
                  </div>
                </div>
                
                <LanguageSelectionPreview 
                  appName={appName}
                  themeColors={themeColors}
                  onboardBgPreview={onboardBgPreview}
                />
              </>
            )}
          </div>
        </div>

        {/* Home Button */}
        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
          <div className="w-12 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>

      {/* Pagination Dots */}
      <div className="flex justify-center space-x-2 mt-4">
        {pages.map((page) => (
          <button
            key={page.id}
            onClick={() => setCurrentPage(page.id)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              currentPage === page.id
                ? 'bg-primary scale-110'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            style={{
              backgroundColor: currentPage === page.id ? primaryColor : undefined
            }}
            title={page.name}
          />
        ))}
      </div>

      <p className="text-sm text-gray-500 text-center max-w-xs">
        This preview shows how your app will look with the selected brand colors
      </p>
    </div>
  );
};

export default MobilePreview;
